import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import authAPI from '@/api/auth';
import { setAuthToken, setAuthTokenCentral } from '@/plugins/axios';
import { usePreferencesStore } from '@/store/preferences';
import { useAlertStore } from "@/store/alertStore"; // Asegúrate de importar el store de alertas
import { useLayoutStore } from '@/store/layout';


export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem('authToken') || '');
  const isAuthenticated = computed(() => !!token.value);
  const user = ref<Record<string, any> | null>(null);
  const permissions = ref<string[]>([]);
  const roles = ref<string[]>([]);

  // Action to set authentication data
  const setAuthData = (newToken: string, newUser: Record<string, any>) => {
    localStorage.setItem('authToken', newToken);
    token.value = newToken;
    user.value = newUser;
    permissions.value = newUser.permissions || [];
    roles.value = newUser.role || [];

    // Update preferences store with user's preferences
    const preferencesStore = usePreferencesStore();
    if (newUser.preferences) {
      preferencesStore.setPreferences(newUser.preferences);
    }
  };

  // Refactored login method that handles authentication
  const login = async (email: string, password: string) => {
    try {
      // Make API call to authenticate user
      const response = await authAPI.login({ email, password });
      const { access_token, user } = response.data.data;

      // Set token and user data in store
      setAuthData(access_token, user);

      // Set the token in headers for future API calls
      setAuthToken(access_token);
      setAuthTokenCentral(access_token);

      return { success: response.data.success, data: response.data.data }; // Indicate successful login
    } catch (error: any) {
        return { success: error.response.data.success, error: error.response.data.message };
      //const alertStore = useAlertStore(); alertStore.showAlert('Login failed. Please check your credentials.', 'error');
      //throw new Error('Login failed. Please check your credentials.');
    }
  };

  // Action to handle logout
  const logout = () => {
    localStorage.removeItem('authToken');
    token.value = '';
    user.value = null;
    permissions.value = [];
    roles.value = [];
    const layoutStore = useLayoutStore();
    layoutStore.$reset();
  };

  // Action to initialize user after login
  const initializeUser = async () => {
    if (!token.value) return;

    try {
      setAuthToken(token.value);
      setAuthTokenCentral(token.value);
      const response = await authAPI.getAuthenticatedUser();
      setAuthData(token.value, response.data.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      logout();
    }
  };

  // Methods for checking permissions and roles
  const hasPermission = (permission: string) => permissions.value.includes(permission);
  const hasRole = (role: string) => roles.value.includes(role);

  const recoverPassword  = async (email: string) => {
    try {
      // Make API call to authenticate user
      const response = await authAPI.recoverPassword({ email });
      return { success: true, data: response.data }; // Indicate successful login
    } catch (error: any) {
        return { success: false, error: error.response.data.message };
    }
  };

  return {
    token,
    isAuthenticated,
    user,
    permissions,
    roles,
    setAuthData,
    login, // Updated login method
    logout,
    initializeUser,
    hasPermission,
    hasRole,
    recoverPassword
  };
});
